/**
 * Scheme dates are stored as UTC instants of the tenant-local day boundary (IST unless company_profile.timezone is set):
 *   start = 00:00 IST of the picked start day        (4 Oct  -> 2026-10-03T18:30:00.000Z)
 *   end   = 00:00 IST of the day AFTER the picked end (10 Oct -> 2026-10-10T18:30:00.000Z, exclusive)
 * Slicing the ISO string gives the UTC date, which is one day early for starts. These helpers
 * return the picked calendar day (YYYY-MM-DD) again. Date-only strings pass through unchanged.
 */
const IST_OFFSET_MS = 5.5 * 3600 * 1000;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// Keep in sync with TENANT_TIMEZONES in backend utils/schemeDateWindow.ts (hours from UTC).
const TZ_HOURS = {
  "Pacific/Honolulu": -10,
  "America/Mexico_City": -6,
  "America/Sao_Paulo": -3,
  "America/Buenos_Aires": -3,
  UTC: 0,
  "Africa/Abidjan": 0,
  "Africa/Accra": 0,
  "Africa/Dakar": 0,
  "Africa/Lagos": 1,
  "Africa/Kinshasa": 1,
  "Africa/Johannesburg": 2,
  "Africa/Harare": 2,
  "Africa/Lusaka": 2,
  "Africa/Nairobi": 3,
  "Africa/Addis_Ababa": 3,
  "Africa/Dar_es_Salaam": 3,
  "Africa/Kampala": 3,
  "Europe/Moscow": 3,
  "Europe/Istanbul": 3,
  "Asia/Dubai": 4,
  "Asia/Kolkata": 5.5,
  "Asia/Karachi": 5,
  "Asia/Tashkent": 5,
  "Asia/Colombo": 5.5,
  "Asia/Kathmandu": 5.75,
  "Asia/Dhaka": 6,
  "Asia/Yangon": 6.5,
  "Asia/Bangkok": 7,
  "Asia/Singapore": 8,
  "Asia/Hong_Kong": 8,
  "Asia/Shanghai": 8,
  "Australia/Perth": 8,
  "Asia/Tokyo": 9,
  "Asia/Seoul": 9,
};

// Tenant day-boundary offset: company_profile.timezone from the logged-in vendorData; missing => IST.
const tenantOffsetMs = () => {
  try {
    const tz = JSON.parse(sessionStorage.getItem("vendorData"))?.timezone;
    return tz in TZ_HOURS ? TZ_HOURS[tz] * 3600 * 1000 : IST_OFFSET_MS;
  } catch {
    return IST_OFFSET_MS;
  }
};

const istDay = (ms) => new Date(ms + tenantOffsetMs()).toISOString().slice(0, 10);

const toDay = (stored, shiftMs) => {
  if (!stored) return "";
  if (typeof stored === "string" && DATE_ONLY.test(stored)) return stored;
  const ms = new Date(stored).getTime();
  return Number.isNaN(ms) ? "" : istDay(ms + shiftMs);
};

/** Picked start day of a stored start instant (scheme_start / redeem_start). */
export const startDay = (stored) => toDay(stored, 0);

/** Picked end day (last active day) of a stored end instant (scheme_end / redeem_end). */
export const endDay = (stored) => toDay(stored, -1);

/** YYYY-MM-DD -> DD/MM/YYYY ("—" when empty). */
export const fmtDmy = (ymd) => {
  const [y, m, d] = (ymd || "").split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
};
